import { useState } from 'react';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { I18nProvider } from '@react-aria/i18n';
import { type Matcher } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
// Only the date field is taken from React Aria. Its Button, Popover and
// Calendar would duplicate primitives this app already owns.
import {
    DateField,
    DateInput,
    DateSegment,
} from 'react-aria-components/DateField';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
    Popover,
    PopoverPositioner,
    PopoverRoot,
    PopoverTrigger,
} from '@/components/Popover';
import { createDateTime, formatTimeFromDate } from '@/utils/date';
import { DateCalendar } from './DateCalendar';
import {
    clampToRange,
    createNarrowWeekdayFormatter,
    fromDateFieldValue,
    toDateFieldValue,
} from './utils';

interface DateTimePickerProps {
    label: string;
    value?: Date;
    onChange: (value?: Date) => void;
    onCommit?: () => void;
    minValue?: Date;
    maxValue?: Date;
    showCalendar?: boolean;
    disabled?: boolean;
    className?: string;
}

// Segment order follows the locale. It is pinned here rather than left to the
// browser, so a US-locale browser does not show MM/DD/YYYY while the rest of
// the app formats dates as dd/MM/yyyy. Switch to i18n.language once a second
// locale ships.
const DATE_FIELD_LOCALE = 'en-GB';

const DEFAULT_TIME = '00:00';

const FIELD_CLASS =
    'flex h-8 items-center gap-1 rounded-[2px] border border-grey-500 px-1.5 font-geist-mono text-xs text-white-100 transition-colors focus-within:border-grey-300 data-[disabled]:cursor-not-allowed data-[disabled]:border-grey-600 data-[disabled]:bg-grey-650 data-[disabled]:text-grey-300';

const SEGMENT_CLASS =
    'rounded-[2px] px-0.5 tabular-nums caret-transparent outline-none data-[placeholder]:text-grey-300 data-[focused]:bg-grey-500 data-[focused]:text-white-100 data-[disabled]:text-grey-300 data-[type=literal]:px-0 data-[type=literal]:text-grey-300';

export function DateTimePicker({
    label,
    value,
    onChange,
    onCommit,
    minValue,
    maxValue,
    showCalendar = false,
    disabled,
    className,
}: DateTimePickerProps) {
    const { t, i18n } = useTranslation();
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(value ?? new Date());

    const disabledDays: Matcher[] = [
        ...(minValue ? [{ before: minValue }] : []),
        ...(maxValue ? [{ after: maxValue }] : []),
    ];

    function handleCalendarOpenChange(open: boolean) {
        if (open) {
            setCalendarMonth(value ?? maxValue ?? new Date());
        }
        setCalendarOpen(open);
    }

    function handleChange(next?: Date) {
        onChange(clampToRange(next, minValue, maxValue));
    }

    function handleCalendarSelect(day: Date | undefined) {
        if (!day) return;
        handleChange(
            createDateTime(day, formatTimeFromDate(value, DEFAULT_TIME)),
        );
        setCalendarOpen(false);
        onCommit?.();
    }

    return (
        <I18nProvider locale={DATE_FIELD_LOCALE}>
            <DateField
                aria-label={label}
                value={toDateFieldValue(value)}
                onChange={(next) => handleChange(fromDateFieldValue(next))}
                onFocusChange={(isFocused) => {
                    if (!isFocused) onCommit?.();
                }}
                minValue={toDateFieldValue(minValue)}
                maxValue={toDateFieldValue(maxValue)}
                granularity="minute"
                hourCycle={24}
                shouldForceLeadingZeros
                isDisabled={disabled}
                className={cn(FIELD_CLASS, className)}
            >
                <DateInput className="flex flex-1 items-center">
                    {(segment) => (
                        <DateSegment
                            segment={segment}
                            className={SEGMENT_CLASS}
                        />
                    )}
                </DateInput>
                {showCalendar && (
                    <PopoverRoot
                        open={calendarOpen}
                        onOpenChange={handleCalendarOpenChange}
                    >
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t('datePicker.openCalendar')}
                                disabled={disabled}
                                className="size-5"
                            >
                                <CalendarBlankIcon className="size-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverPositioner
                            align="end"
                            sideOffset={8}
                            collisionPadding={16}
                        >
                            <Popover className="w-[300px]">
                                <DateCalendar
                                    selected={value}
                                    onSelect={handleCalendarSelect}
                                    month={calendarMonth}
                                    onMonthChange={setCalendarMonth}
                                    disabled={
                                        disabledDays.length > 0
                                            ? disabledDays
                                            : undefined
                                    }
                                    startMonth={minValue}
                                    endMonth={maxValue}
                                    formatWeekdayName={createNarrowWeekdayFormatter(
                                        i18n.language,
                                    )}
                                />
                            </Popover>
                        </PopoverPositioner>
                    </PopoverRoot>
                )}
            </DateField>
        </I18nProvider>
    );
}
